/**
 * Integration of the drawings library with the editor (excalidraw-app).
 *
 * The editor keeps working as upstream: the scene lives in localStorage
 * (elements + appState) and IndexedDB (images). On top of that we track which
 * library drawing is currently open, and mirror every change into the library
 * store, which is then synced with the server.
 */

import { CaptureUpdateAction, reconcileElements } from "@excalidraw/excalidraw";
import { APP_NAME, debounce } from "@excalidraw/common";
import { hashElementsVersion, newElementWith } from "@excalidraw/element";
import { getMany, setMany } from "idb-keyval";
import { useEffect } from "react";

import { clearAppStateForLocalStorage } from "@excalidraw/excalidraw/appState";

import type { RemoteExcalidrawElement } from "@excalidraw/excalidraw/data/reconcile";
import type { ExcalidrawElement } from "@excalidraw/element/types";
import type {
  AppState,
  BinaryFileData,
  BinaryFiles,
  ExcalidrawImperativeAPI,
} from "@excalidraw/excalidraw/types";

import { STORAGE_KEYS } from "../app_constants";
import { importFromLocalStorage } from "../data/localStorage";
import { updateBrowserStateVersion } from "../data/tabSync";

import {
  generateThumbnail,
  getReferencedFileIds,
  loadDrawing,
  newDrawingId,
  pickDrawingAppState,
  restoreDrawingElements,
  saveDrawingContent,
  setDrawingThumbnail,
} from "./drawings";
import { lt } from "./i18n";
import {
  DrawingStore,
  STORED_DRAWING_KEY,
  editorFilesStore,
  onLibraryChange,
} from "./store";
import { SyncEngine } from "./sync";

import type { DrawingId, DrawingMeta, FolderId } from "./types";

const LEGACY_DRAWING_HASH_RE = /^#drawing=([A-Za-z0-9_-]{1,128})$/;
const DRAWING_PATH_RE = /^\/d\/([A-Za-z0-9_-]{1,128})\/?$/;
const NEW_DRAWING_PATH_RE = /^\/new\/?$/;
const FOLDER_PATH_RE = /^\/f\/([A-Za-z0-9_-]{1,128})\/?$/;
/** folders of new drawings, until they're created (on first edit) */
const NEW_DRAWING_FOLDERS_KEY = "excalidraw-library-new-folders";
/** links opening a scene that isn't a library drawing (upstream) */
const EXTERNAL_SCENE_HASH_RE = /^#(room|json|url)=/;
const SAVE_TIMEOUT = 1000;
const THUMBNAIL_TIMEOUT = 1500;

// -----------------------------------------------------------------------------
// routes
//
//   /                       library (root)
//   /f/<id>                 library, folder <id>
//   /d/<id>                 editor, library drawing <id>
//   /new[?folder=<id>]      editor, new drawing (then /d/<id>)
//   /#room=… /#json=…       editor, upstream collaboration / share links
//   /library, /#drawing=<id> legacy, still supported
// -----------------------------------------------------------------------------

export const LIBRARY_PATH = "/";

export const getLibraryPath = (folderId?: FolderId | null) =>
  folderId ? `/f/${folderId}` : LIBRARY_PATH;

/** folder shown by the library page (null: root) */
export const getLibraryFolderFromUrl = (): FolderId | null =>
  window.location.pathname.match(FOLDER_PATH_RE)?.[1] ?? null;

export const getDrawingPath = (id: DrawingId) => `/d/${id}`;

export const NEW_DRAWING_PATH = "/new";

const hasExternalScene = () =>
  EXTERNAL_SCENE_HASH_RE.test(window.location.hash) ||
  new URLSearchParams(window.location.search).has("id");

export const isLibraryPage = () => {
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  if (path === "/library" || FOLDER_PATH_RE.test(path)) {
    return true;
  }
  return (
    path === "/" &&
    !hasExternalScene() &&
    !LEGACY_DRAWING_HASH_RE.test(window.location.hash)
  );
};

type EditorRoute =
  | { type: "drawing"; id: DrawingId }
  | { type: "new"; folderId: FolderId | null }
  /** root URL with an upstream (collaboration, share) link */
  | { type: "external" };

const parseEditorRoute = (): EditorRoute => {
  const legacy = window.location.hash.match(LEGACY_DRAWING_HASH_RE);
  if (legacy) {
    return legacy[1] === "new"
      ? { type: "new", folderId: null }
      : { type: "drawing", id: legacy[1] };
  }
  if (NEW_DRAWING_PATH_RE.test(window.location.pathname)) {
    const folderId = new URLSearchParams(window.location.search).get("folder");
    return {
      type: "new",
      folderId:
        folderId && /^[A-Za-z0-9_-]{1,128}$/.test(folderId) ? folderId : null,
    };
  }
  const match = window.location.pathname.match(DRAWING_PATH_RE);
  return match ? { type: "drawing", id: match[1] } : { type: "external" };
};

// -----------------------------------------------------------------------------
// current drawing
//
// Each tab shows the drawing of its URL (`currentDrawingId`). The scene in
// localStorage (upstream storage) belongs to one drawing at a time
// (`STORED_DRAWING_KEY`): the last tab that edited.
// -----------------------------------------------------------------------------

const readStoredDrawingId = (): DrawingId | null => {
  try {
    return localStorage.getItem(STORED_DRAWING_KEY);
  } catch {
    return null;
  }
};

const writeStoredDrawingId = (id: DrawingId) => {
  try {
    localStorage.setItem(STORED_DRAWING_KEY, id);
  } catch (error) {
    console.error(error);
  }
};

/** last drawing opened in the editor (on this browser) */
export const getLastOpenedDrawingId = readStoredDrawingId;

let currentDrawingId: DrawingId | null = null;
const currentDrawingListeners = new Set<() => void>();

export const getCurrentDrawingId = () => currentDrawingId;

export const subscribeCurrentDrawingId = (listener: () => void) => {
  currentDrawingListeners.add(listener);
  return () => {
    currentDrawingListeners.delete(listener);
  };
};

/** URL of the drawing open in this tab (keeping a collaboration room) */
export const getEditorUrl = () => {
  const room = EXTERNAL_SCENE_HASH_RE.test(window.location.hash)
    ? window.location.hash.startsWith("#room=")
      ? window.location.hash
      : ""
    : "";
  return currentDrawingId
    ? `${window.location.origin}${getDrawingPath(currentDrawingId)}${room}`
    : window.location.origin;
};

const setCurrentDrawingId = (id: DrawingId) => {
  currentDrawingId = id;
  if (!isLibraryPage()) {
    const url = getEditorUrl();
    if (url !== window.location.href) {
      window.history.replaceState(window.history.state, APP_NAME, url);
    }
  }
  currentDrawingListeners.forEach((listener) => listener());
};

/** another tab took over localStorage with a different drawing: this tab
 * must reload its own drawing instead of upstream's tab-sync */
export const isStoredSceneOfAnotherDrawing = () =>
  !!currentDrawingId && readStoredDrawingId() !== currentDrawingId;

/** in-app navigation between drawings, provided by the editor */
let navigateHandler: ((path: string) => void) | null = null;

export const setEditorNavigationHandler = (
  handler: ((path: string) => void) | null,
) => {
  navigateHandler = handler;
};

/** opens a drawing (or "new", created in `folderId`) in the editor */
export const openDrawing = (
  id: DrawingId | "new",
  folderId?: FolderId | null,
) => {
  const path =
    id !== "new"
      ? getDrawingPath(id)
      : folderId
      ? `${NEW_DRAWING_PATH}?folder=${encodeURIComponent(folderId)}`
      : NEW_DRAWING_PATH;
  if (navigateHandler && !isLibraryPage()) {
    window.history.pushState({}, APP_NAME, path);
    navigateHandler(path);
  } else {
    window.location.assign(path);
  }
};

export const isLegacyDrawingHash = () =>
  LEGACY_DRAWING_HASH_RE.test(window.location.hash);

export const isEditorPath = (pathname: string) =>
  DRAWING_PATH_RE.test(pathname) || NEW_DRAWING_PATH_RE.test(pathname);

// -----------------------------------------------------------------------------
// localStorage scene (upstream format)
// -----------------------------------------------------------------------------

const readLocalStorageVersion = (): number => {
  try {
    return JSON.parse(
      localStorage.getItem(STORAGE_KEYS.VERSION_DATA_STATE) || "-1",
    );
  } catch {
    return -1;
  }
};

/** appState saved in localStorage without the per-drawing properties, i.e.
 * the user preferences (theme, tool settings, …) */
const readUserPreferences = (): Record<string, unknown> => {
  let appState: Record<string, unknown> = {};
  try {
    appState = JSON.parse(
      localStorage.getItem(STORAGE_KEYS.LOCAL_STORAGE_APP_STATE) || "{}",
    );
  } catch {
    // ignore
  }
  for (const key of Object.keys(pickDrawingAppState(appState as any))) {
    delete appState[key];
  }
  return appState;
};

const writeLocalStorageScene = (
  id: DrawingId,
  elements: readonly ExcalidrawElement[],
  appState: Record<string, unknown>,
) => {
  try {
    localStorage.setItem(
      STORAGE_KEYS.LOCAL_STORAGE_ELEMENTS,
      JSON.stringify(elements),
    );
    localStorage.setItem(
      STORAGE_KEYS.LOCAL_STORAGE_APP_STATE,
      JSON.stringify(appState),
    );
    writeStoredDrawingId(id);
    updateBrowserStateVersion(STORAGE_KEYS.VERSION_DATA_STATE);
  } catch (error) {
    console.error(error);
  }
};

const filesToMap = (files: readonly (BinaryFileData | undefined)[]) => {
  const map: BinaryFiles = {};
  for (const file of files) {
    if (file) {
      map[file.id] = file;
    }
  }
  return map;
};

/**
 * Saves the scene found in localStorage into its library drawing, when newer.
 * This recovers changes whose (async) library save didn't complete before the
 * page was closed — localStorage is written synchronously by upstream.
 */
const recoverFromLocalStorage = async (id: DrawingId) => {
  const meta = await DrawingStore.getMeta(id);
  if (meta && meta.localSavedAt >= readLocalStorageVersion()) {
    return;
  }
  const { elements, appState } = importFromLocalStorage();
  if (!meta && !elements.length) {
    return;
  }
  let allElements: readonly ExcalidrawElement[] = elements;
  const scene = meta && (await DrawingStore.getScene(id));
  if (scene) {
    // localStorage holds only non-deleted elements: whatever is missing was
    // deleted
    const present = new Set(elements.map((element) => element.id));
    allElements = [
      ...elements,
      ...scene.elements
        .filter((element) => !present.has(element.id))
        .map((element) =>
          element.isDeleted
            ? element
            : newElementWith(element, { isDeleted: true }),
        ),
    ];
  }
  const fileIds = getReferencedFileIds(allElements);
  const files = filesToMap(
    fileIds.length
      ? await getMany<BinaryFileData | undefined>(fileIds, editorFilesStore)
      : [],
  );
  await saveDrawingContent(id, {
    elements: allElements,
    appState: appState ?? {},
    files,
    origin: "editor",
  });
};

/** writes a library drawing into localStorage/IndexedDB, where upstream
 * initialization will load it from */
const loadIntoLocalStorage = async (meta: DrawingMeta) => {
  const drawing = await loadDrawing(meta.id);
  if (!drawing) {
    return false;
  }
  const files = Object.values(drawing.files);
  if (files.length) {
    const now = Date.now();
    await setMany(
      files.map((file) => [file.id, { ...file, lastRetrieved: now }]),
      editorFilesStore,
    );
  }
  writeLocalStorageScene(
    meta.id,
    drawing.scene.elements.filter((element) => !element.isDeleted),
    {
      ...readUserPreferences(),
      ...drawing.scene.appState,
      name: meta.name,
    },
  );
  setCurrentDrawingId(meta.id);
  return true;
};

/** shows an empty drawing (created in the library on first edit) */
const showEmptyDrawing = (id: DrawingId) => {
  writeLocalStorageScene(id, [], readUserPreferences());
  setCurrentDrawingId(id);
};

const readNewDrawingFolders = (): Record<DrawingId, FolderId> => {
  try {
    return JSON.parse(localStorage.getItem(NEW_DRAWING_FOLDERS_KEY) || "{}");
  } catch {
    return {};
  }
};

const writeNewDrawingFolders = (folders: Record<DrawingId, FolderId>) => {
  try {
    localStorage.setItem(NEW_DRAWING_FOLDERS_KEY, JSON.stringify(folders));
  } catch (error) {
    console.error(error);
  }
};

/** switches to a new, empty drawing (in `folderId`, once created) */
export const startNewDrawing = (folderId?: FolderId | null) => {
  const id = newDrawingId();
  if (folderId) {
    writeNewDrawingFolders({ ...readNewDrawingFolders(), [id]: folderId });
  }
  showEmptyDrawing(id);
};

/**
 * Another tab stored a different drawing in localStorage: once its changes
 * are safe in the library, store this tab's scene there again (without
 * touching the editor, so the undo history is kept).
 */
export const reclaimLocalStorage = async (
  excalidrawAPI: ExcalidrawImperativeAPI,
) => {
  const id = currentDrawingId;
  const storedId = readStoredDrawingId();
  if (!id || storedId === id) {
    return;
  }
  if (storedId) {
    await recoverFromLocalStorage(storedId);
  }
  if (currentDrawingId !== id) {
    return;
  }
  const appState = excalidrawAPI.getAppState();
  writeLocalStorageScene(
    id,
    excalidrawAPI.getSceneElements(),
    clearAppStateForLocalStorage(appState) as Record<string, unknown>,
  );
};

/**
 * Called at the beginning of upstream `initializeScene()`: makes sure the
 * localStorage scene is the one of the drawing to open (from the URL).
 *
 * @returns an error message to show, if any
 */
export const prepareInitialScene = async (): Promise<string | null> => {
  await flushSave();

  const route = parseEditorRoute();
  const previousId = currentDrawingId;
  let storedId = readStoredDrawingId();

  try {
    if (storedId) {
      await recoverFromLocalStorage(storedId);
    } else {
      // first run (or upgrade from upstream): adopt the existing scene
      storedId = newDrawingId();
      writeStoredDrawingId(storedId);
      await recoverFromLocalStorage(storedId);
    }

    if (route.type === "new") {
      startNewDrawing(route.folderId);
      return null;
    }

    const targetId = route.type === "drawing" ? route.id : storedId;
    let meta = await DrawingStore.getMeta(targetId);
    if (!meta && navigator.onLine) {
      try {
        if (await SyncEngine.fetchDrawing(targetId)) {
          meta = await DrawingStore.getMeta(targetId);
        }
      } catch (error) {
        console.warn(error);
      }
    }

    if (!meta || meta.purged) {
      if (!meta?.purged && (targetId === storedId || targetId === previousId)) {
        // a drawing never saved because still empty
        if (targetId === storedId) {
          setCurrentDrawingId(targetId);
        } else {
          showEmptyDrawing(targetId);
        }
        return null;
      }
      if (hasExternalScene()) {
        // e.g. someone else's room link: the scene comes from the room
        setCurrentDrawingId(storedId);
        return null;
      }
      startNewDrawing();
      return lt("drawingNotFound");
    }
    if (meta.deleted && route.type === "external") {
      startNewDrawing();
      return null;
    }
    await loadIntoLocalStorage(meta);
  } catch (error: any) {
    console.error(error);
    return error?.message ?? String(error);
  }
  return null;
};

// -----------------------------------------------------------------------------
// saving editor changes into the library
// -----------------------------------------------------------------------------

type SaveJob = {
  id: DrawingId;
  elements: readonly ExcalidrawElement[];
  appState: AppState;
  files: BinaryFiles;
};

let pendingSave: SaveJob | null = null;
let saveQueue: Promise<unknown> = Promise.resolve();
let pendingThumbnail: SaveJob | null = null;

const updateThumbnail = debounce(() => {
  const job = pendingThumbnail;
  pendingThumbnail = null;
  if (!job) {
    return;
  }
  generateThumbnail(job.elements, job.appState, job.files)
    .then((thumbnail) => setDrawingThumbnail(job.id, thumbnail, "editor"))
    .catch((error) => console.warn("thumbnail generation failed", error));
}, THUMBNAIL_TIMEOUT);

const runPendingSave = () => {
  const job = pendingSave;
  pendingSave = null;
  if (!job) {
    return;
  }
  saveQueue = saveQueue
    .then(() =>
      saveDrawingContent(job.id, {
        elements: job.elements,
        appState: job.appState,
        files: job.files,
        folderId: readNewDrawingFolders()[job.id] ?? null,
        origin: "editor",
      }),
    )
    .then((result) => {
      if (!result) {
        return;
      }
      const newFolders = readNewDrawingFolders();
      if (job.id in newFolders) {
        delete newFolders[job.id];
        writeNewDrawingFolders(newFolders);
      }
      if (job.id === currentDrawingId) {
        document.title = `${result.meta.name} - ${APP_NAME}`;
      }
      if (result.contentChanged || !result.meta.thumbnail) {
        pendingThumbnail = job;
        updateThumbnail();
      }
    })
    .catch((error) => console.error("saving drawing failed", error));
};

const debouncedSave = debounce(runPendingSave, SAVE_TIMEOUT);

/** called from the editor's onChange (hot path: keep it cheap) */
export const scheduleSave = (
  elements: readonly ExcalidrawElement[],
  appState: AppState,
  files: BinaryFiles,
) => {
  const id = currentDrawingId;
  if (!id) {
    return;
  }
  if (pendingSave && pendingSave.id !== id) {
    debouncedSave.flush();
  }
  pendingSave = { id, elements, appState, files };
  debouncedSave();
};

/** writes pending changes immediately */
export const flushSave = () => {
  debouncedSave.flush();
  updateThumbnail.flush();
  return saveQueue;
};

// -----------------------------------------------------------------------------
// collaboration
// -----------------------------------------------------------------------------

let joinedCollabRoom = false;

/** a room link was opened: unless it points to one of our own drawings
 * (e.g. a session started from this drawing on another device), the room
 * isn't "ours" */
export const markJoinedCollabRoom = () => {
  const route = parseEditorRoute();
  joinedCollabRoom = !(
    route.type === "drawing" && route.id === currentDrawingId
  );
};

/** called when leaving a room while keeping its contents: when the room
 * wasn't started from the current drawing, keep it as a new drawing instead
 * of overwriting the current one */
export const onCollabStoppedKeepingScene = () => {
  if (joinedCollabRoom) {
    joinedCollabRoom = false;
    startNewDrawing();
  }
};

// -----------------------------------------------------------------------------
// applying library changes (sync, library page) to the open drawing
// -----------------------------------------------------------------------------

/** shows the stored content as is (conflict resolutions, restores) */
const replaceEditorScene = async (
  excalidrawAPI: ExcalidrawImperativeAPI,
  id: DrawingId,
) => {
  const drawing = await loadDrawing(id);
  if (!drawing || id !== currentDrawingId) {
    return;
  }
  // edits made meanwhile were part of what the user chose to replace
  debouncedSave.cancel();
  pendingSave = null;
  excalidrawAPI.addFiles(Object.values(drawing.files));
  excalidrawAPI.updateScene({
    elements: restoreDrawingElements(drawing.scene.elements),
    appState: { name: drawing.meta.name },
    captureUpdate: CaptureUpdateAction.NEVER,
  });
  excalidrawAPI.history.clear();
};

/** merges the stored content into the editor; `notify`: tell the user when
 * the elements changed (content coming from another device or the MCP) */
const applyStoredDrawing = async (
  excalidrawAPI: ExcalidrawImperativeAPI,
  id: DrawingId,
  notify: boolean,
) => {
  const meta = await DrawingStore.getMeta(id);
  if (!meta || meta.deleted || meta.purged) {
    if (meta) {
      // trashed elsewhere: don't resurrect it with further edits
      debouncedSave.cancel();
      pendingSave = null;
      startNewDrawing();
      excalidrawAPI.resetScene();
      excalidrawAPI.setToast({ message: lt("currentDrawingDeleted") });
    }
    return;
  }
  const drawing = await loadDrawing(id);
  if (!drawing || id !== currentDrawingId) {
    return;
  }

  const currentElements = excalidrawAPI.getSceneElementsIncludingDeleted();
  const reconciled = reconcileElements(
    currentElements,
    restoreDrawingElements(drawing.scene.elements) as RemoteExcalidrawElement[],
    excalidrawAPI.getAppState(),
  );

  const loadedFiles = excalidrawAPI.getFiles();
  const missingFiles = Object.values(drawing.files).filter(
    (file) => !loadedFiles[file.id],
  );
  if (missingFiles.length) {
    excalidrawAPI.addFiles(missingFiles);
  }

  const nameChanged = excalidrawAPI.getAppState().name !== meta.name;
  const elementsChanged =
    hashElementsVersion(reconciled) !== hashElementsVersion(currentElements);
  if (!nameChanged && !elementsChanged) {
    return;
  }
  excalidrawAPI.updateScene({
    elements: reconciled,
    appState: nameChanged ? { name: meta.name } : undefined,
    captureUpdate: CaptureUpdateAction.NEVER,
  });
  if (notify && elementsChanged) {
    excalidrawAPI.setToast({
      message: meta.updatedBy
        ? lt("drawingUpdatedBy", { name: meta.updatedBy })
        : lt("drawingUpdated"),
      duration: 4000,
    });
  }
};

/** starts the sync engine and keeps the open drawing up to date */
export const useDrawingLibrary = (
  excalidrawAPI: ExcalidrawImperativeAPI | null,
  isCollaborating: () => boolean,
) => {
  useEffect(() => {
    SyncEngine.start();
  }, []);

  useEffect(() => {
    if (!excalidrawAPI) {
      return;
    }
    return onLibraryChange((event) => {
      const id = currentDrawingId;
      if (
        event.origin === "editor" ||
        !id ||
        !event.ids.includes(id) ||
        isCollaborating()
      ) {
        return;
      }
      (event.origin === "replace"
        ? replaceEditorScene(excalidrawAPI, id)
        : applyStoredDrawing(excalidrawAPI, id, event.origin === "sync")
      ).catch((error) => console.error(error));
    });
  }, [excalidrawAPI, isCollaborating]);
};
